/**
 * Normalizes addBeer callable errors to { id, name, message }.
 * Handles both object array (addBeer response) and string array (legacy).
 */
export function normalizeErrors(responseErrors) {
  const raw = Array.isArray(responseErrors) ? responseErrors : [];
  return raw.map((entry) =>
    typeof entry === 'object' && entry !== null && 'message' in entry
      ? { id: entry.id ?? '', name: entry.name ?? '', message: entry.message ?? '' }
      : { id: '', name: '', message: typeof entry === 'string' ? entry : String(entry) },
  );
}

/**
 * Resolves duplicate-review document id from one duplicatedBeer entry (shape varies by backend).
 */
export function duplicateReviewIdFromEntry(duplicate) {
  if (!duplicate || typeof duplicate !== 'object') {
    return '';
  }
  const id =
    duplicate.beerDuplicateReviewId ??
    duplicate.reviewId ??
    duplicate.duplicateReviewId ??
    duplicate.beer_duplicate_review_id;
  return id != null && String(id).trim() ? String(id).trim() : '';
}

/**
 * Normalizes addBeer API response (or legacy shape) for UI consumers and ImportSummaryView.
 * Output: addedCount, updatedCount, duplicatedCount, errorCount, addedBeers,
 * updatedBeers, duplicatedBeers, errors, status.
 */
export function processImportResponse(response) {
  const empty = { id: '', name: '', abv: null, style: null, description: null };

  const addedBeers = Array.isArray(response.addedBeers)
    ? response.addedBeers.map((beer) => ({ id: beer.id ?? '', name: beer.name ?? '' }))
    : (Array.isArray(response.successfulBeers) ? response.successfulBeers : []).map((name) => ({
        id: '',
        name: String(name),
      }));

  const rawUpdated = Array.isArray(response.updatedBeers) ? response.updatedBeers : [];
  const isObjectShape =
    rawUpdated.length > 0 &&
    rawUpdated.every(
      (item) => item && typeof item === 'object' && ('inputBeer' in item || 'updatedBeer' in item),
    );
  const updatedBeers = isObjectShape
    ? rawUpdated.map((item) => ({
        inputBeer: { ...empty, ...item.inputBeer },
        updatedBeer: { ...empty, ...item.updatedBeer },
      }))
    : rawUpdated.map((name) => ({
        inputBeer: { ...empty, name: String(name) },
        updatedBeer: { ...empty, id: '', name: String(name) },
      }));

  const duplicatedBeers = Array.isArray(response.duplicatedBeers)
    ? response.duplicatedBeers.map((duplicate) => ({
        matchType: duplicate.matchType ?? 'AI',
        confidence: duplicate.confidence ?? 0,
        rationale: duplicate.rationale ?? '',
        inputBeer: { ...empty, ...duplicate.inputBeer },
        matchedBeer: { ...empty, ...duplicate.matchedBeer },
        beerDuplicateReviewId: duplicateReviewIdFromEntry(duplicate),
      }))
    : [
        ...(Array.isArray(response.rejectedBeers) ? response.rejectedBeers : []).map((rejected) => ({
          matchType: 'AI',
          confidence: rejected.confidence ?? 0,
          rationale: rejected.rationale ?? '',
          inputBeer: { ...empty, name: rejected.name ?? '' },
          matchedBeer: {
            ...empty,
            id: rejected.matchedBeerId ?? '',
            name: rejected.matchedBeerName ?? '',
          },
          beerDuplicateReviewId: duplicateReviewIdFromEntry(rejected),
        })),
        ...(Array.isArray(response.reviewBeers) ? response.reviewBeers : []).map((review) => ({
          matchType: 'AI',
          confidence: review.confidence ?? 0,
          rationale: review.rationale ?? '',
          inputBeer: { ...empty, name: review.name ?? '' },
          matchedBeer: {
            ...empty,
            id: review.matchedBeerId ?? '',
            name: review.matchedBeerName ?? '',
          },
          beerDuplicateReviewId: duplicateReviewIdFromEntry(review),
        })),
      ];

  const errors = normalizeErrors(response.errors);

  const topLevelReviewId =
    typeof response.beerDuplicateReviewId === 'string' && response.beerDuplicateReviewId.trim()
      ? response.beerDuplicateReviewId.trim()
      : '';

  const beerDuplicateReviewId =
    topLevelReviewId ||
    duplicatedBeers.map((entry) => entry.beerDuplicateReviewId).find((id) => Boolean(id)) ||
    '';

  const countOrLength = (value, length) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : length;
  };

  return {
    addedCount: countOrLength(response.addedCount, addedBeers.length),
    updatedCount: countOrLength(response.updatedCount, updatedBeers.length),
    duplicatedCount: countOrLength(response.duplicatedCount, duplicatedBeers.length),
    errorCount: countOrLength(response.errorCount, errors.length),
    addedBeers,
    updatedBeers,
    duplicatedBeers,
    errors,
    status: response.status || '',
    beerDuplicateReviewId,
  };
}
